import { Component, OnInit, ChangeDetectionStrategy, Inject, ChangeDetectorRef } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { Observable } from 'rxjs'

import { DestroyService } from '@core/services/destroy.service'
import { AssetsService } from '@app/modules/client/assets/services/assets.service'
import { Asset } from '@shared/models/asset.model'
import { AssetPairUnpairData } from '@app/shared/models/asset-pair-unpair-data'
import { Sensor, SensorTypeIds } from '@shared/models/sensor.model'
import { DeviceSensorsApiService } from '@shared/services/device-sensors-api.service'
import { UtilityService } from '@core/services/utility.service'

@Component({
  selector: 'lin-asset-pair-unpair-dialog',
  templateUrl: './asset-pair-unpair-dialog.component.html',
  styleUrls: ['./asset-pair-unpair-dialog.component.scss'],
  providers: [DestroyService, DeviceSensorsApiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AssetPairUnpairDialogComponent implements OnInit {
  form: FormGroup
  isSubmitting: boolean
  isLoading: boolean
  asset: Asset
  sensor: Sensor

  constructor(
    private readonly fb: FormBuilder,
    private readonly assetsService: AssetsService,
    private readonly deviceSensorsApiService: DeviceSensorsApiService,
    private readonly dialogRef: MatDialogRef<AssetPairUnpairDialogComponent>,
    private readonly destroy$: DestroyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly utilityService: UtilityService,
    @Inject(MAT_DIALOG_DATA) private readonly data: AssetPairUnpairData,
  ) {
    this.asset = data.asset || data.sensor.asset
    this.sensor = data.sensor || data.asset.sensor
  }

  get isPairWithAsset(): boolean {
    return this.data.isPairWithAsset
  }

  get isUnpair(): boolean {
    return this.data.isUnpair
  }

  get title(): string {
    return this.data.title
  }

  ngOnInit() {
    this.initForm()
    if (this.sensor || this.asset) this.setFormValues()
    this.initListenerAssetIdValueChanges()
    this.initListenerSensorIdValueChanges()
  }

  initForm() {
    this.form = this.fb.group({
      assetId: ['', [Validators.required]],
      sensorId: ['', Validators.required],
    })

    if (this.isUnpair) {
      this.form.controls.assetId.disable()
      this.form.controls.sensorId.disable()
      this.form.markAsDirty()
    }

    if (!this.isPairWithAsset) {
      this.form.controls.assetId.disable()
    }

    if (this.isPairWithAsset) {
      this.form.controls.sensorId.disable()
    }
  }

  private setFormValues(): void {
    const { asset, sensor } = this
    const sensorId = this.utilityService.toMacAddress(sensor?.sensorId || asset?.sensor?.sensorId)

    this.form.patchValue(
      {
        assetId: asset?.idNumber || sensor?.asset?.idNumber || '',
        sensorId: sensorId || '',
      },
      { emitEvent: false },
    )
  }

  private initListenerAssetIdValueChanges(): void {
    this.form.controls.assetId.valueChanges
      .pipe(
        tap(() => this.asset && (this.asset = null)),
        filter(Boolean),
        debounceTime(350),
        distinctUntilChanged(),
        tap(() => {
          this.isLoading = true
          this.cdr.markForCheck()
        }),
        switchMap((assetId: string) => this.assetsService.getAssetList({ idNumber: assetId })),
        tap(() => {
          this.isLoading = false
          this.cdr.markForCheck()
        }),
        filter((assets: Asset[]) => assets.length === 1),
        map((assets: Asset[]) => assets[0]),
        filter((asset: Asset) => !asset.sensor),
        takeUntil(this.destroy$),
      )
      .subscribe((asset: Asset) => {
        this.asset = asset
        this.form.controls.assetId.setValue(asset.idNumber, { emitEvent: false })
      })
  }

  private initListenerSensorIdValueChanges(): void {
    this.form.controls.sensorId.valueChanges
      .pipe(
        tap(() => this.sensor && (this.sensor = null)),
        filter(Boolean),
        debounceTime(350),
        distinctUntilChanged(),
        tap(() => {
          this.isLoading = true
          this.cdr.markForCheck()
        }),
        switchMap((sensorId: string) =>
          this.deviceSensorsApiService.getAllSensorsList({
            sensorId: this.utilityService.macAddressToString(sensorId),
            typeIds: [SensorTypeIds.TemperatureAndHumidity, SensorTypeIds.TrackingBeacon],
          }),
        ),
        tap(() => {
          this.isLoading = false
          this.cdr.markForCheck()
        }),
        filter((sensors: Sensor[]) => sensors.length === 1),
        map((sensors: Sensor[]) => sensors[0]),
        filter((sensor: Sensor) => !sensor.asset),
        takeUntil(this.destroy$),
      )
      .subscribe((sensor: Sensor) => {
        this.sensor = sensor
        this.form.controls.sensorId.setValue(this.utilityService.toMacAddress(sensor.sensorId), { emitEvent: false })
      })
  }

  private pairOrUnpairRequest(): Observable<Asset> {
    return this.isUnpair
      ? this.assetsService.unpairWithSensor(this.asset.id, this.sensor.id)
      : this.assetsService.pairWithSensor(this.asset.id, this.sensor.id)
  }

  pairOrUnpairAsset(): void {
    this.isSubmitting = true

    this.pairOrUnpairRequest()
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe(() => this.dialogRef.close(true))
  }
}
